import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  inject
} from '@angular/core';
import {
  PreloadAllModules,
  RouteReuseStrategy,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withViewTransitions
} from '@angular/router';
import { NoReuseStrategy } from './shared/strategies/no-reuse.strategy';
import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideOptimus } from '@openng/optimus-ui/config';
import { MyPreset } from './prime-ng-theme';
import { provideToastr } from 'ngx-toastr';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import {
  ANALYTICS_CONFIG,
  API_CONFIG,
  AnalyticsService,
  ConnectivityService,
  NotificationsRealtimeService,
  connectivityInterceptor,
  dateTransformInterceptor
} from '@zayan-erp/shared';
import { dealLockInterceptor } from '@zayan-erp/sales';
import { provideRealtime } from '@zayan-erp/realtime';
import { BulkDownloadNotificationsService } from '@zayan-erp/file-manager';
import { realtimeTokenProvider } from './realtime/realtime-token.provider';
import { InterceptorService } from './shared/interceptors/interceptor.service';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { appEffects } from './store/app.effects';
import { appReducers } from './store/app.reducers';
import { environment } from '../environments/environment';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    ConfirmationService,
    MessageService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withViewTransitions(), withPreloading(PreloadAllModules)),
    provideHttpClient(),
    provideOptimus({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.app-dark'
        }
      }
    }),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-center',
      preventDuplicates: true,
      closeButton: true,
      progressBar: false,
      progressAnimation: 'increasing',
      tapToDismiss: false
    }),
    provideHttpClient(
      withInterceptors([dateTransformInterceptor, connectivityInterceptor, dealLockInterceptor]),
      withInterceptorsFromDi()
    ),
    { provide: RouteReuseStrategy, useClass: NoReuseStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'MMM d, y, h:mm a' } },
    provideEffects(appEffects),
    provideStore(appReducers),
    // provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    {
      provide: API_CONFIG,
      useValue: {
        baseUrl: environment.baseUrl,
        baseUrlV2: environment.baseUrlV2,
        recaptchaSiteKey: environment.recaptchaSiteKey,
        appName: environment.appName
      }
    },
    {
      provide: ANALYTICS_CONFIG,
      useValue: {
        posthogKey: environment.posthogKey,
        posthogHost: environment.posthogHost,
        environment: environment.analyticsEnvironment
      }
    },
    provideAppInitializer(() => {
      inject(AnalyticsService).init();
      inject(ConnectivityService).start();
      // Injected for its constructor: it subscribes to the realtime feed here, at bootstrap, so
      // bulk-download toasts reach the user on every page rather than only the file manager.
      inject(BulkDownloadNotificationsService);
      //the bell's toast must not depend on a header being on screen.
      inject(NotificationsRealtimeService);
    }),
    // New realtime connection layer. Runs alongside the legacy SignalR services and shares
    // nothing with them: no existing service, component or hub is involved. Off until
    // environment.realtimeEnabled is turned on.
    provideRealtime(
      {
        hubUrl: environment.realtimeHubUrl,
        enabled: environment.realtimeEnabled,
        verbose: !environment.production
      },
      realtimeTokenProvider
    ),
    provideLottieOptions({ player: () => player })
  ]
};
